import { Files, FileStack } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useImperativeHandle } from 'react';

import { Form, FormField } from 'src/components/ui/form';
import { ToggleGroupCard, ToggleGroupCardItem } from 'src/components/ui/toggle-group';
import { AgreementFormEnum } from 'src/graphql/schema';

import { useSoftwareEscrowNewContext } from '../model';

const formSchema = z.object({
  agreementForm: z.string().min(1, { message: 'Required' }),
});

export type IAgreementFormSettingsValues = z.infer<typeof formSchema>;

const AgreementFormSettings = () => {
  const { formRef, setIsValid } = useSoftwareEscrowNewContext();

  const form = useForm<IAgreementFormSettingsValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      agreementForm: '',
    },
    mode: 'onChange',
  });

  useEffect(() => void form.trigger(), []);

  useImperativeHandle(
    formRef,
    () => ({
      submit: (callback) => form.handleSubmit(callback)(),
    }),
    [],
  );

  const isValid = form.formState.isValid;

  useEffect(() => setIsValid(isValid), [isValid, setIsValid]);

  return (
    <Form {...form}>
      <FormField
        control={form.control}
        name="agreementForm"
        render={({ field }) => (
          <ToggleGroupCard
            type="single"
            onValueChange={field.onChange}
            value={field.value}
            orientation="horizontal"
            className="gap-4"
          >
            <ToggleGroupCardItem
              value={AgreementFormEnum.Tripartite}
              icon={FileStack}
              title="Tripartite agreement → Beneficiary IS involved"
              description="We’ll generate a tripartite agreement signed by the depositor, the beneficiary, and the escrow agent. This will outline each party’s responsibilities in full."
              className="flex-1"
            />
            <ToggleGroupCardItem
              value={AgreementFormEnum.Bipartite}
              icon={Files}
              title="Bipartite agreement → Beneficiary is NOT involved"
              description="We'll generate a bipartite agreement between the depositor and the escrow agent. The beneficiary won’t sign, but can still request a release if certain conditions are met."
              className="flex-1"
            />
          </ToggleGroupCard>
        )}
      />
    </Form>
  );
};
export default AgreementFormSettings;
